/**
 * Settings → E-Mail-Einrichtung (#3031): an owner lets single employees set up the farm's mailbox.
 *
 * A grant takes effect at the employee's NEXT login (the BFF session reads it then); a revoke is
 * immediate (the BFF logs the employee out) — the success copy says which. Buttons, not a switch:
 * the revoke has a consequence (a logout) the label should name.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Badge, Button, SettingsPane, SettingsRow, SettingsSection } from '@hofsync/design-system';
import { useToast } from '../../contexts/ToastContext';
import {
    integrationGrantService,
    EMAIL_SETUP_CAPABILITY,
    type GrantableEmployee,
    type IntegrationGrant,
} from '../../services/integrationGrantService';
import type { SettingsPaneChrome } from './paneChrome';

const displayName = (employee: GrantableEmployee): string =>
    [employee.first_name, employee.last_name].filter(Boolean).join(' ').trim() || employee.phone;

export default function EmailDelegationPane({ backLink, focusOnMount }: SettingsPaneChrome) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [grants, setGrants] = useState<IntegrationGrant[]>([]);
    const [employees, setEmployees] = useState<GrantableEmployee[]>([]);
    const [loading, setLoading] = useState(true);
    const [pending, setPending] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await integrationGrantService.getGrants();
            setGrants(response.grants);
            setEmployees(response.employees);
        } catch (error) {
            console.error('Failed to load integration grants:', error);
            showToast(t('settings.emailDelegation.loadError', 'Freigaben konnten nicht geladen werden'), 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast, t]);

    useEffect(() => {
        load();
    }, [load]);

    const grant = async (userId: string) => {
        setPending(userId);
        try {
            await integrationGrantService.createGrant({ grantee_user_id: userId, capability: EMAIL_SETUP_CAPABILITY });
            showToast(
                t(
                    'settings.emailDelegation.grantSuccess',
                    'Freigabe erteilt. Sie wird aktiv, sobald sich der Mitarbeiter das nächste Mal anmeldet.'
                ),
                'success'
            );
            await load();
        } catch (error) {
            console.error('Failed to grant email setup:', error);
            showToast(t('settings.emailDelegation.grantError', 'Freigabe konnte nicht erteilt werden'), 'error');
        } finally {
            setPending(null);
        }
    };

    const revoke = async (userId: string) => {
        const existing = grants.find((g) => g.grantee_user_id === userId && g.capability === EMAIL_SETUP_CAPABILITY);
        if (!existing) return;
        setPending(userId);
        try {
            await integrationGrantService.revokeGrant(existing.id);
            showToast(t('settings.emailDelegation.revokeSuccess', 'Freigabe sofort entzogen'), 'success');
            await load();
        } catch (error) {
            console.error('Failed to revoke email setup:', error);
            showToast(t('settings.emailDelegation.revokeError', 'Freigabe konnte nicht entzogen werden'), 'error');
        } finally {
            setPending(null);
        }
    };

    return (
        <SettingsPane
            title={t('settings.emailDelegation.title', 'E-Mail-Einrichtung freigeben')}
            description={t(
                'settings.emailDelegation.subtitle',
                'Erlaube einzelnen Mitarbeitern, das E-Mail-Postfach für deinen Betrieb einzurichten. Eine Freigabe wird erst mit der nächsten Anmeldung des Mitarbeiters wirksam; ein Entzug wirkt sofort.'
            )}
            backLink={backLink}
            focusOnMount={focusOnMount}
        >
            <SettingsSection title={t('settings.emailDelegation.employees', 'Mitarbeiter')}>
                {loading ? (
                    <SettingsRow label={t('common.loading', 'Wird geladen...')} />
                ) : employees.length === 0 ? (
                    <SettingsRow label={t('settings.emailDelegation.noEmployees', 'Keine Mitarbeiter vorhanden')} />
                ) : (
                    employees.map((employee) => {
                        const granted = employee.granted_capabilities.includes(EMAIL_SETUP_CAPABILITY);
                        const name = displayName(employee);
                        return (
                            <SettingsRow
                                key={employee.user_id}
                                media={<Avatar name={name} />}
                                label={name}
                                description={
                                    granted
                                        ? t('settings.emailDelegation.statusGranted', 'Darf die E-Mail-Einrichtung vornehmen')
                                        : t('settings.emailDelegation.statusNotGranted', 'Keine Freigabe')
                                }
                            >
                                {granted && <Badge tone="success">{t('settings.emailDelegation.granted', 'Freigegeben')}</Badge>}
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={pending === employee.user_id}
                                    onClick={() => (granted ? revoke(employee.user_id) : grant(employee.user_id))}
                                >
                                    {granted
                                        ? t('settings.emailDelegation.revoke', 'Entziehen')
                                        : t('settings.emailDelegation.grant', 'Freigeben')}
                                </Button>
                            </SettingsRow>
                        );
                    })
                )}
            </SettingsSection>
        </SettingsPane>
    );
}
